import { computed, makeAutoObservable, runInAction } from 'mobx';

import { client } from 'contexts/ApolloClient/apolloClient';
import { VALIDATE_AND_OVERLAPS } from 'pages/RideDetails/gql/eligibilityValidation';
import { FETCH_PERFECT_FIT } from 'pages/RideDetails/gql/fetchDrivers';
import { rideDetailsStore } from 'pages/RideDetails/RideDetailsStore';
import { sortCrewMembersByIdleTime } from 'utils/driverIdleSorter';

import { driversWidgetText } from './text';

export class DriverWidgetStore {
	rideDetailsStore;
	eligibleTo = null;

	// observable state
	eligibleDrivers = [];
	filteredDrivers = [];
	showFiltered = false;

	isSearchLoading = false;

	selectedDriver = undefined;
	reasons = [];
	overlaps = [];
	overlappingAction = '';
	delayMinutes = null;
	minDelayMinutes = null;
	maxDelayMinutes = null;
	cannotCancelOverlaps = false;
	showsReasons = false;
	isReassignMode = false;

	loadingEligible = false;
	loadingValidation = false;
	loadingAssignment = false;
	errorEligible = null;

	// internal: prevent duplicate queries for same params
	lastFetchKey = null;

	// { [driverId]: { reasons, overlaps } } — populated on expand, cleared on search close
	bulkValidationMap = {};

	// tracks in-flight validateSingleDriver calls to prevent duplicate concurrent requests
	_pendingValidations = new Set();

	// set to true when startSearch cancels an in-flight fetchEligible
	_eligibleFetchCancelled = false;

	constructor({ rideDetailsStore }) {
		this.rideDetailsStore = rideDetailsStore;
		makeAutoObservable(this, {
			optionsDrivers: computed,
			canProceedWithAssignment: computed,
			isDelayOptionAvailable: computed,
			allowToDeleteOverlaps: computed,
			allowToDelayOverlaps: computed,
			delayError: computed,
			_pendingValidations: false,
			_eligibleFetchCancelled: false,
		});
	}

	// derived
	get optionsDrivers() {
		return this.showFiltered ? this.filteredDrivers : this.eligibleDrivers;
	}

	get eligibilityIssuesByDriverId() {
		if (!this.showFiltered) return {};
		return this.bulkValidationMap || {};
	}

	get isDelayOptionAvailable() {
		return this.minDelayMinutes != null;
	}

	get allowToDeleteOverlaps() {
		return this.overlappingAction === 'delete';
	}

	get allowToDelayOverlaps() {
		return this.overlappingAction === 'delay';
	}

	get delayError() {
		if (!this.allowToDelayOverlaps || this.delayMinutes == null) return null;
		if (this.minDelayMinutes != null && this.delayMinutes < this.minDelayMinutes)
			return driversWidgetText.delayTooSmall;
		if (this.maxDelayMinutes != null && this.delayMinutes > this.maxDelayMinutes)
			return driversWidgetText.delayTooLarge;
		return null;
	}

	get isAssignmentDisabled() {
		return (
			this.loadingAssignment ||
			(this.overlaps.length > 0 && !this.allowToDeleteOverlaps && !this.allowToDelayOverlaps) ||
			this.cannotCancelOverlaps ||
			!!this.delayError
		);
	}

	// helper
	static canFetch(params) {
		return Boolean(
			params?.rideId && params?.fromStationId && params?.toStationId && params?.activityType
		);
	}

	async fetchEligible(params) {
		if (!DriverWidgetStore.canFetch(params)) {
			// why: avoid network call until all inputs are available
			return;
		}

		const key = `${params.rideId}:${params.fromStationId}:${params.toStationId}:${params.activityType}`;
		if (this.lastFetchKey === key) return; // why: no-op if same inputs

		this._eligibleFetchCancelled = false;
		this.lastFetchKey = key;
		this.loadingEligible = true;
		this.errorEligible = null;

		try {
			const { data } = await client.query({
				query: FETCH_PERFECT_FIT,
				fetchPolicy: 'no-cache',
				variables: {
					rideId: params.rideId,
					fromStationId: params.fromStationId,
					toStationId: params.toStationId,
					activityType: params.activityType,
				},
			});

			if (this._eligibleFetchCancelled) return;

			const sorted = sortCrewMembersByIdleTime(data?.eligibleCrewMembersV2 ?? []);
			runInAction(() => {
				this.eligibleDrivers = sorted;
			});
		} catch (e) {
			if (this._eligibleFetchCancelled) return;
			runInAction(() => {
				this.errorEligible = e;
			});
		} finally {
			if (!this._eligibleFetchCancelled) {
				runInAction(() => {
					this.loadingEligible = false;
				});
			}
		}
	}

	cancelFetchEligible() {
		this._eligibleFetchCancelled = true;
		this.loadingEligible = false;
		this.errorEligible = null;
		this.lastFetchKey = null;
	}

	async validateSelection(driver, eligibleTo) {
		this.selectedDriver = driver; // why: show per-item spinner immediately
		this.loadingValidation = true;

		try {
			const { data } = await client.query({
				query: VALIDATE_AND_OVERLAPS,
				fetchPolicy: 'no-cache',
				variables: {
					crewMemberId: driver?.crewMember?.id,
					...eligibleTo,
				},
			});

			const eligibility = data?.eligibility ?? [];
			const overlapsResponse = data?.overlaps ?? {};
			const overlaps = overlapsResponse.overlappingActivities ?? [];

			runInAction(() => {
				this.reasons = eligibility;
				this.overlaps = overlaps;
				this.cannotCancelOverlaps = false;
				this.overlappingAction = '';
				this.minDelayMinutes = overlapsResponse.minDelayMinutes ?? null;
				this.maxDelayMinutes = overlapsResponse.maxDelayMinutes ?? null;
				this.delayMinutes = overlapsResponse.minDelayMinutes ?? null;
				this.showsReasons = eligibility.length > 0 || overlaps.length > 0;
			});

			if (eligibility.length === 0 && overlaps.length === 0) {
				await this.rideDetailsStore.createWorkAssignment(this.selectedDriver.crewMember.id);
				this.close();
			}
		} finally {
			runInAction(() => {
				this.loadingValidation = false;
			});
		}
	}

	async validateSingleDriver(driver, eligibleTo) {
		if (!this.showFiltered) return;

		const driverId = driver?.crewMember?.id;

		if (!driverId) return;

		if (this.bulkValidationMap[driverId] || this._pendingValidations.has(driverId)) return;

		this._pendingValidations.add(driverId);

		try {
			const { data } = await client.query({
				query: VALIDATE_AND_OVERLAPS,
				fetchPolicy: 'no-cache',
				variables: { crewMemberId: driverId, ...eligibleTo },
			});

			const reasons = data?.eligibility ?? [];
			const overlapsResponse = data?.overlaps ?? {};
			const overlaps = overlapsResponse.overlappingActivities ?? [];

			runInAction(() => {
				this.bulkValidationMap[driverId] = { reasons, overlaps };
			});
		} catch {
			return;
		} finally {
			this._pendingValidations.delete(driverId);
		}
	}

	async assignAnyway() {
		if (!this.selectedDriver?.crewMember?.id) return;
		if (this.loadingAssignment) return;

		this.loadingAssignment = true;

		try {
			const overlappingActivityAction = this.allowToDeleteOverlaps
				? 'cancel'
				: this.allowToDelayOverlaps
					? 'delay'
					: null;

			const result = await this.rideDetailsStore.createWorkAssignment(
				this.selectedDriver.crewMember.id,
				{
					overlappingActivityAction,
					delayMinutes: this.allowToDelayOverlaps ? this.delayMinutes : null,
				}
			);

			if (result?.kind === 'overlap' && result?.cannotCancel) {
				runInAction(() => {
					this.overlaps = result.overlappingActivities;
					this.cannotCancelOverlaps = true;
					this.overlappingAction = '';
					this.delayMinutes = null;
					this.minDelayMinutes = null;
					this.maxDelayMinutes = null;
					this.showsReasons = true;
				});
				return;
			}

			if (result?.kind === 'overlap') {
				runInAction(() => {
					this.overlaps = result.overlappingActivities;
					this.cannotCancelOverlaps = false;
					this.overlappingAction = '';
					this.minDelayMinutes = result.minDelayMinutes ?? null;
					this.maxDelayMinutes = result.maxDelayMinutes ?? null;
					this.delayMinutes = result.minDelayMinutes ?? null;
					this.showsReasons = true;
				});
				return;
			}

			this.close();
		} finally {
			runInAction(() => {
				this.loadingAssignment = false;
			});
		}
	}

	setOverlappingAction(value) {
		this.overlappingAction = value;
	}

	setDelayMinutes(value) {
		this.delayMinutes = value;
	}

	setFiltered(list) {
		this.filteredDrivers = Array.isArray(list) ? list : [];
	}

	startSearch() {
		if (this.loadingEligible) {
			this.cancelFetchEligible();
		}

		this.showFiltered = true;
	}

	closeSearch() {
		this.showFiltered = false;
		this.filteredDrivers = [];
		this.bulkValidationMap = {};

		if (this._eligibleFetchCancelled) {
			this._eligibleFetchCancelled = false;
			this.fetchEligible(this.eligibleTo);
		}
	}

	setSearchLoading(v) {
		this.isSearchLoading = Boolean(v);
	}

	setReassignMode(value) {
		this.isReassignMode = value;
	}

	hideReasons() {
		this.showsReasons = false;
	}

	dispose() {}

	close() {
		this.showsReasons = false;
		this.filteredDrivers = [];
		this.selectedDriver = undefined;
		this.showFiltered = false;
		this.reasons = [];
		this.overlaps = [];
		this.overlappingAction = '';
		this.delayMinutes = null;
		this.minDelayMinutes = null;
		this.maxDelayMinutes = null;
		this.cannotCancelOverlaps = false;
		this.loadingAssignment = false;
		this.bulkValidationMap = {};
		this.isReassignMode = false;

		this.rideDetailsStore.exitAssignmentState();
	}

	setEligibleTo(next) {
		this.eligibleTo = next;
	}
}

export const driversWidgetStore = new DriverWidgetStore({ rideDetailsStore });
